import React, {useEffect, useState} from 'react'
import {Box, CircularProgress, Typography} from '@mui/material'
import {Image} from 'antd'
import moment from 'moment'
import AppDialog from 'src/components-new/feedback/AppDialog'
import getFileMIMEtype from 'src/components-new/feedback/getFileMIMEtype'
import {BODY_PARTS_OPTIONS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {CARD_BORDER_COLOR, CARD_BORDER_RADIUS} from 'src/theme-new/tokens'

interface CompareImagesDialogNewProps {
  open: boolean
  onClose: () => void
  images: Record<string, any>[]
}

/**
 * Multi-image side-by-side viewer, opened from the "Confronta" action once
 * two or more image rows are selected. Simplified vs. legacy
 * CompareImagesDialog: drops the manual preload/opacity-fade nicety since it
 * is not core functionality.
 *
 * Each image is fetched through `apiClient` (carrying the mandatory `secret`
 * header) and turned into a blob URL — a raw `<img src>` pointed at
 * `psp-backend/images/download/<file>` cannot carry that header and the
 * backend rejects it, leaving a broken-image icon.
 */
export default function CompareImagesDialogNew({open, onClose, images}: CompareImagesDialogNewProps): JSX.Element {
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open || images.length === 0) {
      setImageUrls({})
      return undefined
    }

    let cancelled = false
    const objectUrls: string[] = []

    Promise.all(images.map(async image => {
      if (!image.file) return
      const fileResponse = await apiClient.downloadPatientImage(image.file, () => {})
      if (cancelled || !fileResponse) return
      const mime = getFileMIMEtype(image.file) ?? 'image/jpeg'
      const blob = new Blob([new Uint8Array(fileResponse)], {type: mime})
      const url = (window.URL || window.webkitURL).createObjectURL(blob)
      objectUrls.push(url)
      setImageUrls(prev => ({...prev, [image._id]: url}))
    }))

    return () => {
      cancelled = true
      objectUrls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [open, images])

  return (
    <AppDialog open={open} onClose={onClose} title="Confronta immagini" maxWidth="lg">
      <Box sx={{display: 'flex', gap: 2, overflowX: 'auto', pb: 1}}>
        {images.map((image) => {
          const bodyPartLabel = BODY_PARTS_OPTIONS.find(option => option.value === image.bodyPart)?.label || image.bodyPart
          const dateLabel = image.plannedDate ? moment(image.plannedDate).format('DD MMM YYYY') : '—'
          const imageUrl = imageUrls[image._id]
          return (
            <Box
              key={image._id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                width: 260,
                border: `1px solid ${CARD_BORDER_COLOR}`,
                borderRadius: CARD_BORDER_RADIUS,
                p: 1.5,
              }}
            >
              {imageUrl ? (
                <Image
                  alt={`${bodyPartLabel}_${image.plannedDate ?? ''}`}
                  preview={{zIndex: 9999}}
                  src={imageUrl}
                  style={{maxWidth: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 4}}
                />
              ) : (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 260}}>
                  <CircularProgress size={24} />
                </Box>
              )}
              <Box sx={{mt: 1, textAlign: 'center'}}>
                <Typography variant="body2" sx={{fontWeight: 600}}>{bodyPartLabel}</Typography>
                <Typography variant="body2" color="text.secondary">{dateLabel}</Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
    </AppDialog>
  )
}
